import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import styles from './index.module.scss';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CustomModal from 'Common/CustomModal';
import LoginFormUI from './LoginFormUI';
import API, { STRIPE_KEY } from 'api';
import { Backdrop, CircularProgress, Box, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { toast } from 'react-toastify';
import moment from 'moment';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from './StripePaymentForm';

const stripePromise = loadStripe(STRIPE_KEY);

const userRole = localStorage.getItem("user-type");
const userId = localStorage.getItem("user_id");
const orgId = localStorage.getItem("org_id");

const PaymentModal = ({ data, isPaymentModalOpen, handleClose }) => {
    // console.log("PaymentModal data => ", data);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [backdrop, setBackdrop] = useState(false);
    const [clientSecret, setClientSecret] = useState('');
    // const [paymentIntentId, setPaymentIntentId] = useState('');
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [redirectCountdown, setRedirectCountdown] = useState(5);
    const [autoPayment, setAutoPayment] = useState(true); // ✅ ADD

    const [userLoginData, setUserLoginData] = useState({
        userRole: userRole,
        userId: userId,
        orgId: orgId,
    });

    useEffect(() => {
        if (userLoginData.userRole && userLoginData.userId && userLoginData.orgId) {
            setIsLoggedIn(true);
        }
    }, [userLoginData.userRole, userLoginData.userId, userLoginData.orgId]);

    // Redirect countdown after payment success
    useEffect(() => {
        if (paymentSuccess && redirectCountdown > 0) {
            const timer = setTimeout(() => {
                setRedirectCountdown(redirectCountdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (paymentSuccess && redirectCountdown === 0) {
            // Redirect to dashboard
            window.location.href = '/dashboard'; // Change to your dashboard route
        }
    }, [paymentSuccess, redirectCountdown]);

    // Create payment intent when user is logged in
    const handleInitiatePayment = async () => {
        if (!userLoginData.userRole || !userLoginData.userId || !userLoginData.orgId) {
            toast.warning("Credentials missing, please contact Organization administrator");
            return;
        }

        try {
            setBackdrop(true);

            // ✅ CHECK: Is this a trial upgrade?
            const isTrialUpgrade = userLoginData.subscriptionPackage === "Trial";

            const payload = {
                subscriptionTemplateId: data.templateId || null,
                orgId: userLoginData.orgId,
                userId: userLoginData.userId,
                userRole: userLoginData.userRole,
                currency: data.currency || 'USD',
                amount: data.amount,
                billAnnually: data.billAnnually,
                startDate: moment().format("YYYY-MM-DD"),
                autoPayment: !data.billAnnually && autoPayment,
                endDate: data.billAnnually
                    ? moment().add(1, "year").format("YYYY-MM-DD")
                    : moment().add(1, "month").format("YYYY-MM-DD")
            };

            console.log("Creating payment intent with payload:", payload);
            console.log(`🎯 Payment type: ${isTrialUpgrade ? 'TRIAL UPGRADE' : 'NEW SUBSCRIPTION'}`);

            // ✅ CALL DIFFERENT API BASED ON TRIAL STATUS
            const res = isTrialUpgrade
                ? await API.pricing.createPaymentIntentForTrialUpgrade(payload)
                : await API.pricing.createPaymentIntent(payload);

            console.log("Payment intent response:", res);

            if (res?.data?.success) {
                setClientSecret(res.data.data.clientSecret);
                console.log(`✓ Payment intent created: ${res.data.data.paymentIntentId}`);

                if (isTrialUpgrade) {
                    toast.info("🎁 Upgrading your trial subscription...");
                }
            } 
        } catch (err) {
            console.error("Error creating payment intent:", err);
            toast.error(err.message || "Failed to initiate payment");
        } finally {
            setBackdrop(false);
        }
    };

    const handlePaymentSuccess = () => {
        console.log("✓ Payment successful!");
        setPaymentSuccess(true);
        toast.success("Payment successful! Your subscription is being processed.");
    };

    const handlePaymentError = (errorMessage) => {
        console.error("❌ Payment failed:", errorMessage);
        toast.error(`Payment failed: ${errorMessage}`);
    };

    const stripeOptions = {
        clientSecret,
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#1976d2',
            }
        },
    };

    const PaymentFormUI = () => {
        // Handle null data
        if (!data) {
            return null;
        }

        // Show success message
        if (paymentSuccess) {
            return (
                <DialogContent>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                    >
                        <CheckCircleIcon
                            sx={{ fontSize: 80, color: 'success.main', mb: 2 }}
                        />
                        <Typography variant="h5" gutterBottom>
                            Payment Successful!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" align="center">
                            Your subscription is being processed.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Redirecting to dashboard in {redirectCountdown} seconds...
                        </Typography>
                    </Box>
                </DialogContent>
            );
        }

        // Show payment form
        if (!clientSecret) {
            return (
                <DialogContent>
                    <div className={styles.header}>
                        <CreditCardIcon color='primary' fontSize='large' />
                        <h4>The Right Plan for Your Business</h4>
                        <span>We have several powerful plans for you to showcase your business</span>
                        <br />
                        <span>and get discovered as creative entrepreneurs.</span>
                    </div>
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        {/* <Typography variant="h6" gutterBottom>
                            {data.subscriptionPackage} - {data.subscriptionPlan}
                        </Typography> */}
                        <Typography variant="h4" color="primary" gutterBottom>
                            ${data.amount} {data.currency}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {data.billAnnually ? 'Billed Annually' : 'Billed Monthly'}
                        </Typography>

                        {/* ✅ ADD AUTO-PAYMENT CHECKBOX (ONLY FOR MONTHLY) */}
                        {!data.billAnnually && (
                            <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={autoPayment}
                                            onChange={(e) => setAutoPayment(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1" fontWeight="bold">
                                                Enable Auto-Payment
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Automatically charge {data.currency} {data.amount} on the 1st of every month
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
            );
        }

        // Show Stripe payment form
        return (
            <DialogContent>
                <div className={styles.header}>
                    <CreditCardIcon color='primary' fontSize='large' />
                    <h4>Complete Your Payment</h4>
                </div>
                <Box sx={{ mt: 3 }}>
                    <Elements stripe={stripePromise} options={stripeOptions}>
                        <StripePaymentForm
                            amount={data.amount}
                            currency={data.currency || 'USD'}
                            onSuccess={handlePaymentSuccess}
                            onError={handlePaymentError}
                        />
                    </Elements>
                </Box>
            </DialogContent>
        );
    };

    return (
        <>
            <CustomModal
                open={isPaymentModalOpen}
                handleClose={handleClose}
                title={!isLoggedIn ? "Please Login to Continue" : "Payment Verification"}
                ariaLabelledby='Payment Verification Modal'
            >
                {!isLoggedIn ? (
                    <LoginFormUI handleClose={handleClose} setUserLoginData={setUserLoginData} />
                ) : (
                    <>
                        <PaymentFormUI />
                        {!clientSecret && !paymentSuccess && (
                            <DialogActions>
                                <Button onClick={handleClose}>Cancel</Button>
                                <Button
                                    onClick={handleInitiatePayment}
                                    variant="contained"
                                >
                                    Proceed to Payment
                                </Button>
                            </DialogActions>
                        )}
                    </>
                )}

                {backdrop && (
                    <Backdrop open={backdrop} style={{ zIndex: 9999 }}>
                        <CircularProgress sx={{ color: "#fff" }} />
                    </Backdrop>
                )}
            </CustomModal>
        </>
    );
};

export default PaymentModal; 